"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  CheckboxRow,
  GuardedPrintButton,
  CompletionPanel,
  TableScroll,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* Site induction pack builder.
   CDM 2015 reg 13(4) puts a duty on the principal contractor to give every
   worker a site-SPECIFIC induction before they start — covering project risks
   (incl. Schedule 3 high-risk activities), the management structure, welfare,
   first aid, accident/near-miss reporting and emergency arrangements. This tool
   assembles those sections into a printable pack plus an attendance/signature
   sheet, which is the record that the induction was actually delivered. */

interface SiteDetails {
  name: string;
  address: string;
  manager: string;
  managerContact: string;
  hours: string;
  parking: string;
}

interface HazardRow {
  id: number;
  hazard: string;
  control: string;
}

const STANDARD_RULES: { id: string; label: string }[] = [
  { id: "ppe", label: "Mandatory PPE worn at all times in work areas (hard hat, hi-vis, safety footwear; task-specific PPE as required)" },
  { id: "signin", label: "Everyone signs in on arrival and signs out before leaving site" },
  { id: "areas", label: "No entry to unauthorised or barriered-off areas without permission" },
  { id: "phones", label: "No mobile phones or earphones while operating plant, machinery or near moving vehicles" },
  { id: "tidy", label: "Keep work areas clean and tidy — clear access routes and remove waste as you go" },
  { id: "smoking", label: "Smoking and vaping only in the designated area — nowhere else on site" },
  { id: "drugs", label: "Zero tolerance on drugs and alcohol; report any prescribed medication that may affect work" },
  { id: "young", label: "Young persons and visitors must be signed in and escorted at all times" },
];

const HAZARD_PRESETS: { hazard: string; control: string }[] = [
  { hazard: "Site traffic and deliveries", control: "Use marked pedestrian routes; keep clear of reversing vehicles; obey banksman/marshal" },
  { hazard: "Work at height overhead", control: "Watch for falling objects; do not enter exclusion zones below height work; wear head protection" },
  { hazard: "Open excavations", control: "Keep clear of edges and supports; cross only at provided points; no plant near unprotected edges" },
  { hazard: "Asbestos in situ", control: "Do not disturb — known/presumed ACMs are marked; stop and report any suspect material immediately" },
  { hazard: "Live services (electric / gas / water)", control: "Treat all services as live; no digging or drilling without a permit and service drawings" },
  { hazard: "Occupied areas / public nearby", control: "Maintain segregation and barriers; keep noise and disruption to a minimum; no unattended openings" },
  { hazard: "Noise and dust zones", control: "Wear hearing and respiratory protection in marked zones; use on-tool extraction and water suppression" },
];

let nextHazardId = 1;
function emptyHazard(): HazardRow {
  return { id: nextHazardId++, hazard: "", control: "" };
}

export function SiteInductionBuilder() {
  const [site, setSite] = useState<SiteDetails>({
    name: "",
    address: "",
    manager: "",
    managerContact: "",
    hours: "",
    parking: "",
  });
  const [rules, setRules] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(STANDARD_RULES.map((r) => [r.id, true]))
  );
  const [welfare, setWelfare] = useState({
    toilets: "",
    washing: "",
    rest: "",
    water: "",
  });
  const [emergency, setEmergency] = useState({
    assembly: "",
    alarm: "",
    extinguishers: "",
    firstAiders: "",
    ae: "",
  });
  const [hazards, setHazards] = useState<HazardRow[]>([emptyHazard()]);
  const [permits, setPermits] = useState("");
  const [reporting, setReporting] = useState("");
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const resultTracked = useRef(false);

  const updateHazard = (id: number, patch: Partial<HazardRow>) =>
    setHazards((hs) => hs.map((h) => (h.id === id ? { ...h, ...patch } : h)));

  const addPreset = (preset: { hazard: string; control: string }) =>
    setHazards((hs) => {
      const blank = hs.find((h) => !h.hazard.trim() && !h.control.trim());
      if (blank) {
        return hs.map((h) => (h.id === blank.id ? { ...h, ...preset } : h));
      }
      return [...hs, { id: nextHazardId++, ...preset }];
    });

  const selectedRules = STANDARD_RULES.filter((r) => rules[r.id]);
  const filledHazards = useMemo(
    () => hazards.filter((h) => h.hazard.trim() || h.control.trim()),
    [hazards]
  );

  const hasSiteDetails = Object.values(site).some((v) => v.trim());
  const hasWelfare = Object.values(welfare).some((v) => v.trim());
  const hasEmergency = Object.values(emergency).some((v) => v.trim());
  const hasAnything =
    hasSiteDetails ||
    selectedRules.length > 0 ||
    hasWelfare ||
    hasEmergency ||
    filledHazards.length > 0 ||
    permits.trim().length > 0 ||
    reporting.trim().length > 0;

  // Core sections an induction pack shouldn't ship without: who/where, the
  // safety-critical emergency arrangements, the site-specific hazards and how
  // to report. Welfare detail and optional rules stay optional.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Site name", done: site.name.trim().length > 0 },
    { label: "Site manager", done: site.manager.trim().length > 0 },
    { label: "Fire assembly point", done: emergency.assembly.trim().length > 0 },
    { label: "Alarm signal", done: emergency.alarm.trim().length > 0 },
    { label: "First aiders", done: emergency.firstAiders.trim().length > 0 },
    { label: "At least one site hazard", done: filledHazards.length > 0 },
    { label: "Reporting arrangements", done: reporting.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  // Fire the funnel event once per visit when the pack first has content.
  useEffect(() => {
    if (hasAnything && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("site-induction", { hazards: filledHazards.length });
    }
  }, [hasAnything, filledHazards.length]);

  // Build the ordered list of sections that actually have content.
  const sections: { heading: string; body: React.ReactNode }[] = [];

  if (hasSiteDetails) {
    sections.push({
      heading: "Site details",
      body: (
        <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
          {site.name.trim() && <PackRow label="Site" value={site.name} />}
          {site.address.trim() && <PackRow label="Address" value={site.address} />}
          {site.manager.trim() && <PackRow label="Site manager" value={site.manager} />}
          {site.managerContact.trim() && <PackRow label="Contact" value={site.managerContact} />}
          {site.hours.trim() && <PackRow label="Working hours" value={site.hours} />}
          {site.parking.trim() && <PackRow label="Parking / access" value={site.parking} />}
        </dl>
      ),
    });
  }

  if (selectedRules.length > 0) {
    sections.push({
      heading: "Site rules",
      body: (
        <ul className="ml-5 list-disc space-y-1 text-body">
          {selectedRules.map((r) => (
            <li key={r.id}>{r.label}</li>
          ))}
        </ul>
      ),
    });
  }

  if (hasWelfare) {
    sections.push({
      heading: "Welfare",
      body: (
        <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
          {welfare.toilets.trim() && <PackRow label="Toilets" value={welfare.toilets} />}
          {welfare.washing.trim() && <PackRow label="Washing facilities" value={welfare.washing} />}
          {welfare.rest.trim() && <PackRow label="Rest area" value={welfare.rest} />}
          {welfare.water.trim() && <PackRow label="Drinking water" value={welfare.water} />}
        </dl>
      ),
    });
  }

  if (hasEmergency) {
    sections.push({
      heading: "Emergency arrangements",
      body: (
        <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
          {emergency.assembly.trim() && <PackRow label="Fire assembly point" value={emergency.assembly} />}
          {emergency.alarm.trim() && <PackRow label="Alarm signal" value={emergency.alarm} />}
          {emergency.extinguishers.trim() && <PackRow label="Extinguisher locations" value={emergency.extinguishers} />}
          {emergency.firstAiders.trim() && <PackRow label="First aiders" value={emergency.firstAiders} />}
          {emergency.ae.trim() && <PackRow label="Nearest A&E" value={emergency.ae} />}
        </dl>
      ),
    });
  }

  if (filledHazards.length > 0) {
    sections.push({
      heading: "Key site hazards",
      body: (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[0.78rem] uppercase tracking-wide text-muted">
              <th className="py-1.5 pr-3 font-semibold">Hazard</th>
              <th className="py-1.5 font-semibold">What inductees must know</th>
            </tr>
          </thead>
          <tbody>
            {filledHazards.map((h) => (
              <tr key={h.id} className="border-b border-line/60 align-top">
                <td className="py-2 pr-3 font-semibold text-ink">{h.hazard.trim() || "—"}</td>
                <td className="py-2 text-body">{h.control.trim() || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
    });
  }

  if (permits.trim()) {
    sections.push({
      heading: "Permits & restricted activities",
      body: <p className="whitespace-pre-line text-body">{permits.trim()}</p>,
    });
  }

  if (reporting.trim()) {
    sections.push({
      heading: "Reporting",
      body: <p className="whitespace-pre-line text-body">{reporting.trim()}</p>,
    });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
      {/* Inputs */}
      <div className="space-y-6">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Site details</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Site name" className="sm:col-span-2">
              <TextInput
                value={site.name}
                onChange={(e) => setSite({ ...site, name: e.target.value })}
                placeholder="e.g. Riverside Apartments, Phase 2"
              />
            </Field>
            <Field label="Site address" className="sm:col-span-2">
              <TextInput
                value={site.address}
                onChange={(e) => setSite({ ...site, address: e.target.value })}
                placeholder="Street, town, postcode"
              />
            </Field>
            <Field label="Site manager">
              <TextInput
                value={site.manager}
                onChange={(e) => setSite({ ...site, manager: e.target.value })}
                placeholder="Name"
              />
            </Field>
            <Field label="Manager contact">
              <TextInput
                value={site.managerContact}
                onChange={(e) => setSite({ ...site, managerContact: e.target.value })}
                placeholder="Phone / radio channel"
              />
            </Field>
            <Field label="Working hours">
              <TextInput
                value={site.hours}
                onChange={(e) => setSite({ ...site, hours: e.target.value })}
                placeholder="e.g. 08:00–17:00 Mon–Fri"
              />
            </Field>
            <Field label="Parking / access">
              <TextInput
                value={site.parking}
                onChange={(e) => setSite({ ...site, parking: e.target.value })}
                placeholder="Where to park, gate / entry point"
              />
            </Field>
          </div>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Site rules</h2>
          <p className="mt-1 text-sm text-muted">
            Tick the standard rules that apply. Each adds a line to the induction pack — add site-specific rules
            under the hazards or permits sections below.
          </p>
          <div className="mt-4 space-y-2">
            {STANDARD_RULES.map((r) => (
              <CheckboxRow
                key={r.id}
                checked={!!rules[r.id]}
                onChange={(v) => setRules((rs) => ({ ...rs, [r.id]: v }))}
              >
                {r.label}
              </CheckboxRow>
            ))}
          </div>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Welfare</h2>
          <p className="mt-1 text-sm text-muted">Where inductees find the welfare facilities on this site.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Toilets">
              <TextInput value={welfare.toilets} onChange={(e) => setWelfare({ ...welfare, toilets: e.target.value })} placeholder="Location" />
            </Field>
            <Field label="Washing facilities">
              <TextInput value={welfare.washing} onChange={(e) => setWelfare({ ...welfare, washing: e.target.value })} placeholder="Location" />
            </Field>
            <Field label="Rest area / canteen">
              <TextInput value={welfare.rest} onChange={(e) => setWelfare({ ...welfare, rest: e.target.value })} placeholder="Location" />
            </Field>
            <Field label="Drinking water">
              <TextInput value={welfare.water} onChange={(e) => setWelfare({ ...welfare, water: e.target.value })} placeholder="Location" />
            </Field>
          </div>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Emergency arrangements</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Fire assembly point">
              <TextInput value={emergency.assembly} onChange={(e) => setEmergency({ ...emergency, assembly: e.target.value })} placeholder="e.g. Main gate car park" />
            </Field>
            <Field label="Alarm signal">
              <TextInput value={emergency.alarm} onChange={(e) => setEmergency({ ...emergency, alarm: e.target.value })} placeholder="e.g. Continuous air horn" />
            </Field>
            <Field label="Extinguisher locations">
              <TextInput value={emergency.extinguishers} onChange={(e) => setEmergency({ ...emergency, extinguishers: e.target.value })} placeholder="Where extinguishers are sited" />
            </Field>
            <Field label="First aiders">
              <TextInput value={emergency.firstAiders} onChange={(e) => setEmergency({ ...emergency, firstAiders: e.target.value })} placeholder="Name(s) + how to reach them" />
            </Field>
            <Field label="Nearest A&E" className="sm:col-span-2" hint="Not sure? Use the free Nearest A&E finder, then paste the details here.">
              <TextInput value={emergency.ae} onChange={(e) => setEmergency({ ...emergency, ae: e.target.value })} placeholder="Hospital name, address and route" />
            </Field>
          </div>
          <a
            href="/tools/nearest-ae-finder/"
            className="mt-3 inline-block text-[0.85rem] font-semibold text-emerald-deep hover:underline"
          >
            Find the nearest A&E for this site →
          </a>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Key site hazards</h2>
          <p className="mt-1 text-sm text-muted">
            The hazards specific to this site and the control each inductee must know. Use the quick-add presets
            or write your own.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {HAZARD_PRESETS.map((p) => (
              <button
                key={p.hazard}
                type="button"
                onClick={() => addPreset(p)}
                className="rounded-full border border-line-2 px-3 py-1.5 text-[0.8rem] font-semibold text-ink hover:border-ink hover:bg-mist"
              >
                + {p.hazard}
              </button>
            ))}
          </div>
          <div className="mt-4 space-y-3">
            {hazards.map((h, i) => (
              <div key={h.id} className="rounded-xl border border-line bg-mist/60 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Hazard {i + 1}</span>
                  {hazards.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setHazards((hs) => hs.filter((x) => x.id !== h.id))}
                      className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="mt-3 grid gap-3">
                  <Field label="Hazard">
                    <TextInput
                      value={h.hazard}
                      onChange={(e) => updateHazard(h.id, { hazard: e.target.value })}
                      placeholder="e.g. Live overhead power lines"
                    />
                  </Field>
                  <Field label="Control inductees must know">
                    <TextInput
                      value={h.control}
                      onChange={(e) => updateHazard(h.id, { control: e.target.value })}
                      placeholder="e.g. Goal posts and exclusion zone — no plant beyond barriers"
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setHazards((hs) => [...hs, emptyHazard()])}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            + Add another hazard
          </button>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Permits & restricted activities</h2>
          <p className="mt-1 text-sm text-muted">
            Which tasks need a permit to work on this site (hot works, confined space, excavation near services,
            isolation, etc.) and who issues them.
          </p>
          <div className="mt-4">
            <Field label="">
              <TextArea
                value={permits}
                onChange={(e) => setPermits(e.target.value)}
                placeholder="e.g. Hot works require a permit from the site manager. No excavation without a permit and service drawings."
              />
            </Field>
          </div>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Reporting</h2>
          <p className="mt-1 text-sm text-muted">
            How to report near misses, accidents and defects, and who to tell.
          </p>
          <div className="mt-4">
            <Field label="">
              <TextArea
                value={reporting}
                onChange={(e) => setReporting(e.target.value)}
                placeholder="e.g. Report all near misses and accidents to the site manager immediately. Defective plant or equipment must be tagged out and reported before further use."
              />
            </Field>
          </div>
        </div>

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* Output */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Pack completion" />
        </div>
        {hasAnything ? (
          <>
            <div className="print-area card shadow-soft p-6" id="induction-pack">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Site induction pack
              </div>
              <h2 className="mt-1 text-lg">{site.name.trim() || "Site induction"}</h2>
              {site.address.trim() && <p className="text-sm text-muted">{site.address.trim()}</p>}
              <p className="mt-1 text-[0.8rem] text-muted">
                Prepared {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>

              <div className="mt-5 space-y-5">
                {sections.map((s, i) => (
                  <section key={s.heading}>
                    <h3 className="font-display text-[0.95rem] font-bold text-ink">
                      {i + 1}. {s.heading}
                    </h3>
                    <div className="mt-1.5 text-sm">{s.body}</div>
                  </section>
                ))}
              </div>

              {/* Attendance / signature sheet */}
              <section className="mt-6 border-t border-line pt-5">
                <h3 className="font-display text-[0.95rem] font-bold text-ink">
                  {sections.length + 1}. Attendance &amp; signature sheet
                </h3>
                <p className="mt-1 text-[0.82rem] text-muted">
                  Everyone inducted must sign below to confirm they understood the induction.
                </p>
                <TableScroll>
                  <table className="mt-3 w-full min-w-[660px] border-collapse text-[0.8rem]">
                    <thead>
                      <tr className="text-left text-[0.72rem] uppercase tracking-wide text-muted">
                        <th className="border border-line px-2 py-1.5 font-semibold">Name</th>
                        <th className="border border-line px-2 py-1.5 font-semibold">Company</th>
                        <th className="border border-line px-2 py-1.5 font-semibold">Trade</th>
                        <th className="border border-line px-2 py-1.5 font-semibold">CSCS / card no.</th>
                        <th className="border border-line px-2 py-1.5 font-semibold">Signature</th>
                        <th className="border border-line px-2 py-1.5 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <tr key={i}>
                          <td className="border border-line px-2 py-3" />
                          <td className="border border-line px-2 py-3" />
                          <td className="border border-line px-2 py-3" />
                          <td className="border border-line px-2 py-3" />
                          <td className="border border-line px-2 py-3" />
                          <td className="border border-line px-2 py-3" />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
                <p className="mt-3 text-[0.8rem] leading-relaxed text-muted">
                  Re-induction is required when the site rules, layout or emergency arrangements change, and for
                  anyone returning after a long break.
                </p>
              </section>

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="site induction builder" />
            </div>
            <GuardedPrintButton
              tool="site-induction"
              label="Print / save pack as PDF"
              missing={missingRequired}
              warningTitle="This induction pack still has empty core sections"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Fill in the site details, rules and hazards on the left to build a printable induction pack with an
            attendance and signature sheet.
          </div>
        )}
      </div>
    </div>
  );
}

function PackRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[0.72rem] font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-body">{value.trim()}</dd>
    </div>
  );
}
